import styled, { createGlobalStyle, css } from 'styled-components';

import media from '../../../utils/styling/media';
import CloseButton from '../../buttons/_CloseButton';
import T3 from '../../titles/T3';

export const DrawerGlobalStyles = createGlobalStyle<{
  scrollOffset?: number;
  drawerId?: number;
  position?: string;
  minWidth?: number;
  maxWidth?: number;
  isOldIOS?: boolean;
  noOverlay?: boolean;
  showModalXs?: boolean;
}>`
  .ReactModal__Body--open {
    overflow: hidden;
    position: relative;
    margin-right: ${({ scrollOffset }) => scrollOffset ? `${scrollOffset}px` : ''};
  }

  .iA_Core_Drawer${({ drawerId }) => drawerId}-Overlay,
  .iA_Core_Drawer${({ drawerId }) => drawerId}-Content {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  .iA_Core_Drawer${({ drawerId }) => drawerId}-Overlay {
    z-index: 100000;
    -webkit-overflow-scrolling: touch;
    transition: background ease-in-out .25s;
    background: rgba(0, 0, 0, 0);

    ${({ noOverlay }) => !noOverlay && css`
      overflow-y: auto;
      overflow-x: hidden;

      &.ReactModal__Overlay--after-open {
        background: ${({ theme }) => theme?.Modal?.overlayBackground};
      }

      &.ReactModal__Overlay--before-close {
        background: rgba(0, 0, 0, 0);
      }
    `}

    ${({ noOverlay }) => noOverlay && css`
      overflow-y: hidden;
      pointer-events: none;
    `}
  }

  .iA_Core_Drawer${({ drawerId }) => drawerId} {
    position: absolute;
    font-family: ${({ theme }) => theme.fontFamily.regular};
    background: ${({ theme }) => theme.colors.white};
    min-width: ${({ minWidth }) => minWidth}px;
    width: 100%;
    max-width: ${({ maxWidth }) => maxWidth}px;
    min-height: 100%;
    border-radius: 0;
    outline: 0;
    transition: transform ease-in-out .25s;
    display: flex;
    flex-direction: column;
    pointer-events: all;
    ${({ showModalXs }) => showModalXs && css`
      ${media.sm<{ minWidth?: number; maxWidth?: number}>`
        min-width: ${({ minWidth }) => minWidth}px;
        max-width: ${({ maxWidth }) => maxWidth}px;
      `}
      max-width: none;
      min-height: none;
        `}

    ${media.lg`
      width: 340px;
    `}

    ${({ position }) => (position === 'left') && css`
      transform: translate3d(-100%, 0, 0);
      left: 0;

    `}

    ${({ position }) => (position === 'right') && css<{ isOldIOS?: boolean }>`
      // Starting not completely from the offset helps old version of iOS from having a rendering issue. It not visibly as the transition is quick enough.
      ${({ isOldIOS }) => isOldIOS && css`
        transform: translate3d(calc(100% - 32px), 0, 0);
      `}
      ${({ isOldIOS }) => !isOldIOS && css`
        transform: translate3d(100%, 0, 0);
      `}
      right: 0;
    `}

    &.ReactModal__Content--after-open {
      transform: translate3d(0, 0, 0);
    }

    &.ReactModal__Content--before-close {
      ${({ position }) => (position === 'left') && css`
        transform: translate3d(-100%, 0, 0);
      `}

      ${({ position }) => (position === 'right') && css`
        transform: translate3d(100%, 0, 0);
      `}
    }

    ${({ noOverlay }) => noOverlay && css`
      overflow-y: auto;
      max-height: 100%;
    `}
  }
`;

export const StyledDrawerHeader = styled.header<{
  noPadding: boolean;
  headerBottomBorder: boolean | string;
  headerPadding: string;
  sticky?: boolean;
}>`
  display: flex;
  justify-content: space-between;
  padding-top: 32px;
  padding-bottom: 36px;
  padding: ${({ noPadding, headerPadding }) => {
    if (headerPadding) return headerPadding;
    return (
      (noPadding) ? '0' : '32px 16px 36px 16px'
    );
  }};
  border-bottom: ${({ headerBottomBorder, theme }) => {
    if (headerBottomBorder === true) return `1px solid ${theme.colors.gray3}`;
    if (headerBottomBorder) return headerBottomBorder;
    return undefined;
  }};

  ${({ sticky, theme }) => sticky && css`
    position: sticky;
    top: 0;
    background-color: ${theme.colors.white};
    z-index: 1;
  `}
`;

export const StyledTitle = styled(T3)`
  flex: 1;
  margin-bottom: 0px;
`;
export const StyledTitlesWrapper = styled.div`
  margin-right: 16px;
`;

export const StyledParagraph = styled.p`
  margin: 16px 0px 0px 0px;
  font-size: 14px;
`;

export const StyledCloseButton = styled(CloseButton)`
  margin: -12px;
`;

export const StyledDrawerContentWrapper = styled.div`
  flex-shrink: 0;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

export const StyledDrawerContent = styled.div<{noPadding?: boolean; noHeader?: boolean; hasFooter?: boolean; contentPadding: string }>`
  flex-shrink: 0;
  padding: ${({ noPadding, noHeader, hasFooter, contentPadding }) => {
    if (contentPadding) return contentPadding;
    if (noPadding) return '0';
    const topPadding = noHeader ? '36px' : '0';
    const bottomPadding = hasFooter ? '16px' : '36px';
    return `${topPadding} 16px ${bottomPadding} 16px`;
  }};
`;

export const StyledFooter = styled.footer<{noPadding?: boolean}>`
  border-top: 1px solid ${({ theme }) => theme.colors.gray3};
  padding: ${({ noPadding }) => (
    noPadding ? '0' : '36px 16px'
  )};
  flex-shrink: 0;
  flex-grow: 1;
`;
